import React, { useContext, useEffect, useState } from 'react';
import { DContext } from '../../../context/DataContext';
import Loading from '../../blocks/Loading';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BsArrowLeft, BsCalendar3, BsEnvelope, BsGeoAlt, BsPersonCircle, BsTelephone, BsShieldLock } from 'react-icons/bs';
import { formatContact } from '../../../utils/contact';

function User() {
  const { t } = useTranslation();
  const { BeURL, setLoader } = useContext(DContext);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const { id } = useParams();

  const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
  };

  const getInitials = (name = '') => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return 'A';
    return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join('');
  };

  useEffect(() => {
    if (BeURL && id) {
      setLoading(true);
      setNotFound(false);
      setLoader(true);
      fetch(`${BeURL}/fetch-user/${id}`, {
        credentials: "include"
      })
        .then(res => res.json())
        .then(data => {
          setLoader(false);
          setLoading(false);
          if (data.success) {
            setUser(data.user);
            setNotFound(false);
          } else {
            setUser(null);
            setNotFound(true);
            alert(t('adminCommon.requestFailed'));
          }
        })
        .catch(err => {
          setLoader(false);
          setLoading(false);
          console.log("Error in fetching user:", err);
          setNotFound(true);
          alert(t('adminCommon.serverError'));
        });
    }
  }, [BeURL, id, setLoader, t]);

  if (loading) {
    return <Loading />;
  }

  if (notFound || !user) {
    return (
      <div className="mx-auto my-6 max-w-2xl rounded-3xl border border-slate-200 bg-white p-6 text-center shadow-[0_16px_38px_rgba(15,23,42,0.08)] sm:p-8">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
          <BsPersonCircle className="text-3xl" />
        </div>
        <h3 className="text-xl font-bold text-slate-900">{t('user.details')}</h3>
        <p className="mt-2 text-sm text-slate-600">{t('user.notFound')}</p>
        <Link to="/admin/users" className="mt-5 inline-flex items-center justify-center gap-2 rounded-2xl bg-[#1739bd] px-4 py-2 text-sm font-semibold text-white no-underline transition hover:bg-indigo-700">
          <BsArrowLeft />
          {t('user.back')}
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto my-2 max-w-5xl space-y-4">
      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_16px_38px_rgba(15,23,42,0.08)]">
        <div className="border-b border-slate-100 bg-slate-50 px-5 py-4 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Link to="/admin/users" className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 no-underline transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-[#1739bd]">
              <BsArrowLeft />
              {t('user.back')}
            </Link>
            <p className="text-sm text-slate-500">{t('user.reviewMetadata')}</p>
          </div>
        </div>

        <div className="bg-gradient-to-r from-[#1739bd] via-[#2047d8] to-[#1739bd] px-5 py-3.5 text-white sm:px-6 sm:py-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-white/20 bg-white/15 text-2xl font-extrabold uppercase tracking-tight text-white">
                {getInitials(user.fullname)}
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/75">{t('user.details')}</p>
                <h3 className="mt-1 text-2xl font-extrabold tracking-tight sm:text-3xl">{user.fullname || t('user.unknown')}</h3>
                <p className="mt-1 text-sm text-white/80">{user.email || '-'}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-white">
                {user.role || t('user.defaultRole')}
              </span>
              <span className="inline-flex items-center rounded-full bg-emerald-500/20 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-emerald-50">
                {t('user.idBadge', { id })}
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-4 p-4 sm:p-6">
          <div className="grid gap-3 self-start sm:grid-cols-2 xl:grid-cols-3">
            <div className="self-start rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                <BsPersonCircle />
                {t('user.fullName')}
              </div>
              <p className="mt-2 break-words text-base font-bold text-slate-900">{user.fullname || '-'}</p>
            </div>
            <div className="self-start rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                <BsEnvelope />
                {t('user.email')}
              </div>
              <p className="mt-2 break-words text-base font-bold text-slate-900">{user.email || '-'}</p>
            </div>
            <div className="self-start rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                <BsGeoAlt />
                {t('user.city')}
              </div>
              <p className="mt-2 break-words text-base font-bold text-slate-900">{user.city || '-'}</p>
            </div>
            <div className="self-start rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                <BsGeoAlt />
                {t('user.country')}
              </div>
              <p className="mt-2 break-words text-base font-bold text-slate-900">{user.country || '-'}</p>
            </div>
            <div className="self-start rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                <BsTelephone />
                {t('user.contact')}
              </div>
              <p className="mt-2 break-words text-base font-bold text-slate-900">{formatContact(user.contact)}</p>
            </div>
            <div className="self-start rounded-2xl border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                <BsShieldLock />
                {t('user.role')}
              </div>
              <p className="mt-2 break-words text-base font-bold text-slate-900">{user.role || '-'}</p>
            </div>
          </div>

          <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 sm:p-4">
            <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              <BsCalendar3 />
              {t('user.accountTimeline')}
            </p>
            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-2xl bg-white px-3 py-2.5 shadow-sm">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{t('user.createdAt')}</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">{formatDate(user.createdAt)}</p>
              </div>
              <div className="rounded-2xl bg-white px-3 py-2.5 shadow-sm">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{t('user.updatedAt')}</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">{formatDate(user.updatedAt)}</p>
              </div>
              <div className="rounded-2xl bg-white px-3 py-2.5 shadow-sm">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{t('user.recordId')}</p>
                <p className="mt-1 break-all text-sm font-semibold text-slate-900">{user._id || id}</p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-indigo-100 bg-indigo-50 px-3 py-3 text-sm text-slate-700">
            <p className="font-semibold text-[#1739bd]">{t('user.profileSummaryTitle')}</p>
            <p className="mt-2 leading-6">
              {t('user.profileSummaryDescription')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default User;
